import React, { useState, useRef } from 'react';
import './App.css';
import FileExplorer from './components/FileExplorer';
import CodeEditor from './components/CodeEditor';
import ComparisonComponent from './components/ComparisonComponent.jsx';

function parseSimilarityReports(outputText) {
  const reports = outputText.trim().split('--------------------------').filter(Boolean);
  const results = [];

  for (const report of reports) {
    const fileMatch               = report.match(/File:\s*(\S+)/);
    const comparingAgainstMatch   = report.match(/Comparing against:\s*(\S+)/);
    const originalMatch           = report.match(/Total original subtrees:\s*(\d+)/);
    const suspectMatch            = report.match(/Total suspect subtrees:\s*(\d+)/);
    const weightedMatch           = report.match(/Weighted Matching Score:\s*(\d+)/);
    const totalScoreMatch         = report.match(/Total Score:\s*(\d+)/);
    const similarityMatch         = report.match(/Similarity:\s*([\d.]+)%/);

    if (fileMatch && similarityMatch) {
      const detailObj = {
        filename:            fileMatch[1],
        comparingAgainst:    comparingAgainstMatch?.[1] || '',
        originalSubtrees:    parseInt(originalMatch?.[1] || '0', 10),
        suspectSubtrees:     parseInt(suspectMatch?.[1] || '0', 10),
        weightedScore:       parseInt(weightedMatch?.[1] || '0', 10),
        totalScore:          parseInt(totalScoreMatch?.[1] || '0', 10),
        similarity:          parseFloat(similarityMatch[1]),
      };

      results.push({
        filename:   detailObj.filename,
        similarity: detailObj.similarity,
        details:    detailObj
      });
    }
  }

  return results;
}

function App() {
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [comparisonResult, setComparisonResult] = useState('');
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentLabel, setCurrentLabel] = useState('');
  const [viewMode, setViewMode] = useState('code');
  const [graphData, setGraphData] = useState([]);

  const fileInputRef = useRef(null);

  const startComparison = () => {
    setIsLoading(true);
    setComparisonResult('');
  };

  const handleNewComparison = (resultString, comparisonKey, label) => {
    setComparisonResult(resultString);
    setCurrentLabel(label);

    const parsed = parseSimilarityReports(resultString);

    setHistory(h => {
      if (h.some(item => item.id === comparisonKey)) return h;
      return [...h, { id: comparisonKey, label, result: resultString, graphData: parsed }];
    });

    setGraphData(parsed);
    setViewMode('comparison');
    setIsLoading(false);
  };

  const handleFilesInput = async (event) => {
    const filesList = event.target.files;
    const newFiles = [];
  
    const formData = new FormData();
  
    for (let i = 0; i < filesList.length; i++) {
      const file = filesList[i];
      const reader = new FileReader();
  
      // For frontend code view
      reader.readAsText(file);
      await new Promise(resolve => {
        reader.onload = e => {
          newFiles.push({ name: file.name, content: e.target.result });
          resolve();
        };
      });
  
      // For backend upload
      formData.append('files', file);
    }
  
    setFiles(prev => [...prev, ...newFiles]);
    if (newFiles.length) setSelectedFile(newFiles[0]);
  
    const res = await fetch('https://detrace-ecdnbwfkawftghga.centralindia-01.azurewebsites.net/upload', {
      method: 'POST',
      body: formData // no headers, browser sets it
    });
  
    if (!res.ok) {
      console.error('Upload failed:', await res.text());
    }
  };

  const handleFileDelete = fileToDelete => {
    setFiles(prev => prev.filter(f => f !== fileToDelete));
    if (selectedFile === fileToDelete) setSelectedFile(files[0] || null);
  };

  const handleFileSelect = file => {
    setSelectedFile(file);
    setComparisonResult('');
    setCurrentLabel('');
    setGraphData([]);
    setViewMode('code');
  };

  const handleButtonClick = () => fileInputRef.current.click();

  return (
    <div className="app-container">
      <div className="sidebar">
        <FileExplorer files={files} onFileSelect={handleFileSelect} onFileDelete={handleFileDelete} />
        <ComparisonComponent filePaths={files.map(f => f.name)} onComparisonStart={startComparison} onComparisonResult={handleNewComparison} />
        <div className="file-input-area">
          <input type="file" multiple ref={fileInputRef} style={{ display: 'none' }} onChange={handleFilesInput} />
          <button onClick={handleButtonClick} className="button">Load Files</button>
        </div>
      </div>

      <div className="main-content">
        <CodeEditor
          isLoading={isLoading}
          content={viewMode === 'comparison' ? comparisonResult : (selectedFile ? selectedFile.content : '')}
          searchResults={searchResults}
          displayLabel={viewMode === 'comparison' ? currentLabel : (selectedFile ? selectedFile.name : '')}
          graphData={viewMode === 'comparison' ? graphData : []}
        />
      </div>

      <div className="comparison-history">
        <h2>Comparisons</h2>
        {history.length === 0
          ? <p>No comparisons yet.</p>
          : <ul>{history.map(item => (
              <li key={item.id} className="history-item">
                <button className="history-btn" onClick={() => {
                  setComparisonResult(item.result);
                  setCurrentLabel(item.label);
                  setGraphData(item.graphData || []);
                  setViewMode('comparison');
                }}>{item.label}</button>
              </li>
            ))}</ul>
        }
      </div>
    </div>
  );
}

export default App;